import { useState, useEffect } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useConfig } from '@/contexts/ConfigContext';
import { useAdminApi } from '@/hooks/useAdminApi';
import toast from 'react-hot-toast';
import { Save, Plus, X } from 'lucide-react';
import type { AppConfig } from '@/types';

export function AdminConfiguration() {
  const { config, refreshConfig } = useConfig();
  const apiClient = useAdminApi();
  const [formData, setFormData] = useState<Partial<AppConfig>>({});
  const [hasChanges, setHasChanges] = useState(false);
  const [newTag, setNewTag] = useState('');

  // Fetch full admin config on mount to get all settings including tags
  useEffect(() => {
    refreshConfig();
  }, [refreshConfig]);

  useEffect(() => {
    setFormData({
      portal_name: config.portal_name,
      logo_light_url: config.logo_light_url,
      logo_dark_url: config.logo_dark_url,
      logo_mark_light_url: config.logo_mark_light_url,
      logo_mark_dark_url: config.logo_mark_dark_url,
      logo_width: config.logo_width,
      favicon_url: config.favicon_url,
      enable_voting: config.enable_voting,
      enable_comments: config.enable_comments,
      enable_projects: config.enable_projects,
      allow_anonymous_submissions: config.allow_anonymous_submissions,
      available_tags: config.available_tags || [],
      email_notifications_enabled: config.email_notifications_enabled,
      notify_on_new_item: config.notify_on_new_item,
      notify_on_status_change: config.notify_on_status_change,
      notify_on_assignment: config.notify_on_assignment,
    });
  }, [config]);

  const updateMutation = useMutation({
    mutationFn: (data: Partial<AppConfig>) => apiClient.updateConfig(data),
    onSuccess: () => {
      refreshConfig();
      setHasChanges(false);
      toast.success('Configuration saved');
    },
    onError: () => {
      toast.error('Failed to save configuration');
    },
  });

  const handleChange = (key: keyof AppConfig, value: any) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
    setHasChanges(true);
  };

  const handleSave = () => {
    updateMutation.mutate(formData);
  };

  const addTag = () => {
    const tag = newTag.trim();
    if (tag && !formData.available_tags?.includes(tag)) {
      handleChange('available_tags', [...(formData.available_tags || []), tag]);
      setNewTag('');
    }
  };

  const removeTag = (tagToRemove: string) => {
    handleChange(
      'available_tags',
      formData.available_tags?.filter((t) => t !== tagToRemove) || []
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Configuration</h1>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
            Manage portal settings and branding
          </p>
        </div>
        <button
          onClick={handleSave}
          disabled={!hasChanges || updateMutation.isPending}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Save className="mr-2 h-4 w-4" />
          {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
        </button>
      </div>

      {/* Branding section */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Branding</h2>
        </div>
        <div className="px-6 py-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Portal Name
            </label>
            <input
              type="text"
              value={formData.portal_name || ''}
              onChange={(e) => handleChange('portal_name', e.target.value)}
              placeholder="Product Portal"
              className="w-full max-w-md px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Logo (Light Mode)
              </label>
              <input
                type="url"
                value={formData.logo_light_url || ''}
                onChange={(e) => handleChange('logo_light_url', e.target.value)}
                placeholder="https://..."
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Logo (Dark Mode)
              </label>
              <input
                type="url"
                value={formData.logo_dark_url || ''}
                onChange={(e) => handleChange('logo_dark_url', e.target.value)}
                placeholder="https://..."
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Logo Mark (Light)
              </label>
              <input
                type="url"
                value={formData.logo_mark_light_url || ''}
                onChange={(e) => handleChange('logo_mark_light_url', e.target.value)}
                placeholder="https://..."
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Square icon for mobile/collapsed sidebar
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Logo Mark (Dark)
              </label>
              <input
                type="url"
                value={formData.logo_mark_dark_url || ''}
                onChange={(e) => handleChange('logo_mark_dark_url', e.target.value)}
                placeholder="https://..."
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Favicon URL
              </label>
              <input
                type="url"
                value={formData.favicon_url || ''}
                onChange={(e) => handleChange('favicon_url', e.target.value)}
                placeholder="https://..."
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Logo Width (px)
              </label>
              <input
                type="number"
                value={formData.logo_width || 120}
                onChange={(e) => handleChange('logo_width', parseInt(e.target.value, 10))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Tags section */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Tags</h2>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Define the available tags that users can apply to their submissions
          </p>
        </div>
        <div className="px-6 py-4 space-y-4">
          <div className="flex gap-2">
            <input
              type="text"
              value={newTag}
              onChange={(e) => setNewTag(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addTag();
                }
              }}
              placeholder="Enter a new tag..."
              className="flex-1 max-w-md px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
            <button
              type="button"
              onClick={addTag}
              disabled={!newTag.trim()}
              className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Plus className="h-4 w-4 mr-1" />
              Add Tag
            </button>
          </div>

          {formData.available_tags && formData.available_tags.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {formData.available_tags.map((tag) => (
                <span
                  key={tag}
                  className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-primary-100 dark:bg-primary-900/50 text-primary-800 dark:text-primary-200"
                >
                  {tag}
                  <button
                    type="button"
                    onClick={() => removeTag(tag)}
                    className="ml-2 text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-200"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </span>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-500 dark:text-gray-400 italic">
              No tags defined. Add tags above to make them available for user submissions.
            </p>
          )}
        </div>
      </div>

      {/* Features section */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Features</h2>
        </div>
        <div className="px-6 py-4 space-y-4">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={formData.enable_voting ?? true}
              onChange={(e) => handleChange('enable_voting', e.target.checked)}
              className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            <span className="ml-3 text-sm text-gray-700 dark:text-gray-300">
              Enable voting on items
            </span>
          </label>
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={formData.enable_comments ?? true}
              onChange={(e) => handleChange('enable_comments', e.target.checked)}
              className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            <span className="ml-3 text-sm text-gray-700 dark:text-gray-300">
              Enable comments on items
            </span>
          </label>
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={formData.enable_projects ?? true}
              onChange={(e) => handleChange('enable_projects', e.target.checked)}
              className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            <span className="ml-3 text-sm text-gray-700 dark:text-gray-300">
              Enable project grouping
            </span>
          </label>
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={formData.allow_anonymous_submissions ?? false}
              onChange={(e) => handleChange('allow_anonymous_submissions', e.target.checked)}
              className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            <span className="ml-3 text-sm text-gray-700 dark:text-gray-300">
              Allow anonymous submissions (no login required)
            </span>
          </label>
        </div>
      </div>

      {/* Notifications section */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Notifications</h2>
        </div>
        <div className="px-6 py-4 space-y-4">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={formData.email_notifications_enabled ?? true}
              onChange={(e) => handleChange('email_notifications_enabled', e.target.checked)}
              className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            <span className="ml-3 text-sm text-gray-700 dark:text-gray-300">
              Enable email notifications
            </span>
          </label>
          <div className="ml-6 space-y-2">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.notify_on_new_item ?? true}
                onChange={(e) => handleChange('notify_on_new_item', e.target.checked)}
                disabled={!formData.email_notifications_enabled}
                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 disabled:opacity-50"
              />
              <span className="ml-3 text-sm text-gray-600 dark:text-gray-400">
                Notify admins on new item submissions
              </span>
            </label>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.notify_on_status_change ?? true}
                onChange={(e) => handleChange('notify_on_status_change', e.target.checked)}
                disabled={!formData.email_notifications_enabled}
                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 disabled:opacity-50"
              />
              <span className="ml-3 text-sm text-gray-600 dark:text-gray-400">
                Notify item author on status changes
              </span>
            </label>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.notify_on_assignment ?? true}
                onChange={(e) => handleChange('notify_on_assignment', e.target.checked)}
                disabled={!formData.email_notifications_enabled}
                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 disabled:opacity-50"
              />
              <span className="ml-3 text-sm text-gray-600 dark:text-gray-400">
                Notify users when assigned to an item
              </span>
            </label>
          </div>
        </div>
      </div>
    </div>
  );
}
